import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, filter } from 'rxjs';

import { WebsocketService } from '@core/services/websocket.service';
import { CONFIG_TOKEN } from '@core/tokens';

import { ChargerCommandType } from './charger-details.enums';
import {
  ChargerActivityResponse,
  ChargerActivityWebsocketMessage,
  ChargerCommandRequest,
  ChargingSessionPriorityResponse,
  ChargingTimelineResponse,
  ChargingTimelineWebsocketMessage,
  EvseActivityResponse,
  EvseActivityWebsocketMessage,
  EvseCommandRequest,
  GetChargerConfigurationResponse,
  ManualCommandExecutionResponse,
  SetChargingSessionPriorityRequest,
} from './charger-details.models';

@Injectable({
  providedIn: 'root',
})
export class ChargerDetailsService {
  readonly #ROOT_URL = inject(CONFIG_TOKEN).backendRootUrl;

  readonly #httpClient = inject(HttpClient);
  readonly #websocketService = inject(WebsocketService);

  public getChargerActivity$(chargerId: string): Observable<ChargerActivityResponse> {
    return this.#httpClient.get<ChargerActivityResponse>(`${this.#ROOT_URL}/api/v1/charger/${chargerId}/activity/recent`);
  }

  public getChargerActivityWebsocketMessage$(chargerId: string): Observable<ChargerActivityWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<ChargerActivityWebsocketMessage>(`charger/${chargerId}/activity/recent`)
      .pipe(filter((message) => message.type === 'charger-recent-activity'));
  }

  public getEvseActivity$(evseId: string): Observable<EvseActivityResponse> {
    return this.#httpClient.get<EvseActivityResponse>(`${this.#ROOT_URL}/api/v1/evse/${evseId}/activity/recent`);
  }

  public getEvseActivityWebsocketMessage$(evseId: string): Observable<EvseActivityWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<EvseActivityWebsocketMessage>(`evse/${evseId}/activity/recent`)
      .pipe(filter((message) => message.type === 'evse-recent-activity'));
  }

  public getChargerActivityTimeline$(chargerId: string, limit: number, beforeTime?: string): Observable<ChargingTimelineResponse> {
    let queryParams: HttpParams = new HttpParams().set('limit', limit);

    if (beforeTime) {
      queryParams = queryParams.set('before', beforeTime);
    }

    return this.#httpClient.get<ChargingTimelineResponse>(`${this.#ROOT_URL}/api/v1/charger/${chargerId}/activity/timeline`, {
      params: queryParams,
    });
  }

  public getChargerActivityTimelineWebsocketMessage$(chargerId: string): Observable<ChargingTimelineWebsocketMessage> {
    const topic = `charger/${chargerId}/activity/timeline`;
    return this.#websocketService
      .connectToTopic$<ChargingTimelineWebsocketMessage>(topic)
      .pipe(filter((message) => message.type === 'charging-timeline' && message.topic === topic));
  }

  public getEvseActivityTimeline$(evseId: string, limit: number, beforeTime?: string): Observable<ChargingTimelineResponse> {
    let queryParams: HttpParams = new HttpParams().set('limit', limit);

    if (beforeTime) {
      queryParams = queryParams.set('before', beforeTime);
    }

    return this.#httpClient.get<ChargingTimelineResponse>(`${this.#ROOT_URL}/api/v1/evse/${evseId}/activity/timeline`, {
      params: queryParams,
    });
  }

  public getEvseActivityTimelineWebsocketMessage$(evseId: string): Observable<ChargingTimelineWebsocketMessage> {
    const topic = `evse/${evseId}/activity/timeline`;
    return this.#websocketService
      .connectToTopic$<ChargingTimelineWebsocketMessage>(topic)
      .pipe(filter((message) => message.type === 'charging-timeline' && message.topic === topic));
  }

  public sendChargerCommand$(chargerId: string, request: ChargerCommandRequest): Observable<ManualCommandExecutionResponse> {
    return this.#httpClient.patch<ManualCommandExecutionResponse>(`${this.#ROOT_URL}/api/v1/charger/${chargerId}/command`, request);
  }

  public sendEvseCommand$(evseId: string, request: EvseCommandRequest): Observable<ManualCommandExecutionResponse> {
    return this.#httpClient.patch<ManualCommandExecutionResponse>(`${this.#ROOT_URL}/api/v1/evse/${evseId}/command`, request);
  }

  public getChargerConfiguration$(chargerId: string): Observable<GetChargerConfigurationResponse> {
    return this.#httpClient.get<GetChargerConfigurationResponse>(`${this.#ROOT_URL}/api/v1/charger/${chargerId}/configuration`);
  }

  public changeChargerConfiguration$(chargerId: string, keyValues: Record<string, string>): Observable<ManualCommandExecutionResponse> {
    return this.sendChargerCommand$(chargerId, { type: ChargerCommandType.ChangeConfiguration, keyValues });
  }

  public setChargingSessionPriority$(
    evseId: string,
    request: SetChargingSessionPriorityRequest
  ): Observable<ChargingSessionPriorityResponse> {
    return this.#httpClient.patch<ChargingSessionPriorityResponse>(
      `${this.#ROOT_URL}/api/v1/evse/${evseId}/charging-session-priority`,
      request
    );
  }
}
